// JEGY-HOZZÁFÉRÉS szerkesztése — a terv beúszó panelje. Ki nyithatja: a jegy NYITÓJA vagy a fiók ELSŐDLEGES
// kapcsolattartója (TULAJDONLÁS, nem szerep) — a döntést a szerver adja készen (`canManageAccess`).
//
// A LISTA a FIÓK kapcsolattartói; a jelölés azt jelenti: „lássa ezt a jegyet". Az ELSŐDLEGES kapcsolattartó
// jelölése ZÁROLT: ő a fiók minden jegyét látja (a láthatósági mag 1. ága), tehát a hozzáférés-sor
// értelmetlen lenne rajta — a felület ezt kimondja, nem csendben tiltja.
//
// A mentés TELJES állapotot küld (a kimaradó hozzáférés visszavonódik); a diffet a szerver számolja.
import type { PortalTicketDetail } from '@atlas/contracts';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DomainBadge } from '@/components/shared/domain-badge';
import { PersonChip } from '@/components/shared/person-chip';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { Text } from '@/components/ui/text';
import { portalKeys } from '@/features/portal/api';
import { api } from '@/lib/api';
import { useEntityMutation } from '@/lib/use-entity-mutation';

export interface PortalTicketAccessSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ticketId: string;
  contacts: PortalTicketDetail['contacts'];
}

export function PortalTicketAccessSheet({
  open,
  onOpenChange,
  ticketId,
  contacts,
}: PortalTicketAccessSheetProps): JSX.Element {
  const { t } = useTranslation();
  // A jelölés-állapot a panel MEGNYITÁSAKOR a szerver állapotából indul (nem tartunk el tőle piszkozatot).
  const [selected, setSelected] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (!open) return;
    setSelected(new Set(contacts.filter((c) => c.hasAccess).map((c) => c.identityId)));
  }, [open, contacts]);

  const mutation = useEntityMutation<{ identityIds: string[] }>({
    mutationFn: (v) => api.portal.ticketAccess.mutation({ params: { id: ticketId }, body: v }),
    invalidateKeys: [portalKeys.ticketDetail(ticketId)],
    messages: {
      success: t('toast:portal.accessSaved'),
      conflict: t('toast:portal.error'),
      forbidden: t('toast:portal.forbidden'),
      validation: t('toast:portal.validation'),
      error: t('toast:portal.error'),
    },
  });

  // Az ELSŐDLEGES kapcsolattartót nem lehet ki/bejelölni (mindent lát) — a listán jelöléssel magyarázzuk.
  // A HÍVÓ SAJÁT SORA sem választható: a saját hozzáférését senki nem vonhatja meg (a terv is kizárja a
  // listából, és a szerver is a hívót mindig hozzáférőnek tekinti a láthatósági mag szerint).
  const selectable = contacts.filter((c) => !c.isPrimary && !c.isSelf);
  const primaries = contacts.filter((c) => c.isPrimary || c.isSelf);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        // MOBILON TELJES SZÉLESSÉG, deszktopon fix panel — a repo beúszó-geometriája (`FormSheet`).
        className="flex flex-col p-0"
      >
        <div className="border-border border-b p-4">
          <SheetTitle>{t('portal.tickets.access')}</SheetTitle>
        </div>

        {/* `text-sm`: a `PersonChip` a KÖRNYEZŐ tipográfiát örökli (ez szándékos), a beúszó panel alap
            szövegmérete viszont 15px — a nevek ezért voltak túl nagyok (user-mérés, 2026-09-04). */}
        <div className="min-h-0 flex-1 overflow-y-auto p-4 text-sm">
          <Text variant="secondary" as="p" className="leading-relaxed">
            {t('portal.tickets.accessHint')}
          </Text>

          {primaries.length > 0 && (
            <div className="mt-4 space-y-2">
              {primaries.map((c) => (
                <div
                  key={c.identityId}
                  className="border-border bg-muted/40 flex items-center gap-2.5 rounded-[9px] border px-3 py-2.5"
                >
                  <PersonChip
                    name={c.name}
                    givenName={c.givenName}
                    familyName={c.familyName}
                    avatarUrl={c.avatarUrl}
                  />
                  <span className="ml-auto flex items-center gap-1.5">
                    {c.isSelf && (
                      <DomainBadge kind="marker" value="you" label={t('portal.tickets.you')} />
                    )}
                    <DomainBadge
                      kind="marker"
                      value="accessAlways"
                      label={t('portal.tickets.accessAlways')}
                    />
                  </span>
                </div>
              ))}
            </div>
          )}

          <div className="mt-3 space-y-2">
            {selectable.length === 0 ? (
              <Text variant="secondary" as="p">
                {t('portal.tickets.accessNoContacts')}
              </Text>
            ) : (
              selectable.map((c) => {
                const on = selected.has(c.identityId);
                return (
                  <label
                    key={c.identityId}
                    className="border-border hover:bg-accent flex cursor-pointer items-center gap-2.5 rounded-[9px] border px-3 py-2.5 transition-colors"
                    style={on ? { borderColor: 'rgb(var(--brand))' } : undefined}
                  >
                    <Checkbox
                      checked={on}
                      onCheckedChange={() =>
                        setSelected((prev) => {
                          const next = new Set(prev);
                          if (next.has(c.identityId)) next.delete(c.identityId);
                          else next.add(c.identityId);
                          return next;
                        })
                      }
                      aria-label={c.name}
                    />
                    <PersonChip
                      name={c.name}
                      givenName={c.givenName}
                      familyName={c.familyName}
                      avatarUrl={c.avatarUrl}
                    />
                  </label>
                );
              })
            )}
          </div>
        </div>

        <div className="border-border bg-muted/40 flex justify-end gap-2 border-t p-3">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="brand"
            size="sm"
            disabled={mutation.isPending}
            onClick={() =>
              mutation.mutate(
                { identityIds: [...selected] },
                { onSuccess: () => onOpenChange(false) },
              )
            }
          >
            {mutation.isPending ? t('portal.tickets.sending') : t('common.save')}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
